import { TFilterOptions } from '@evinced-private/ui-common';

import omit from 'lodash-es/omit';
import { preprocessSearchValue } from 'src/apps/mobile-common/helpers/MobileSearchHelper';
import { buildSeverityFilterVariables } from 'src/apps/mobile-common/helpers/MobileSeverityFilterHelper';
import { IMobileSdkTestsFilterOption } from 'src/common/helpers/DataFilterHelper';
import { QueryVariablesBuilder } from 'src/common/helpers/GQLRequestsHelper/QueryVariablesBuilder';
import {
	ConditionOperators,
	Msdk_Report_RunsTextSearchFields as MsdkReportRunsTextSearchFields
} from 'src/common/types/graphql.generated';

import { MobileSdkUsageDataQueryVariables } from '../MobileSdkUsageData.generated';

const DAYS_AMOUNT_FILTER_PARAM_KEY = 'daysAmountFilter';
const DAYS_AMOUNT_X2_FILTER_PARAM_KEY = 'daysAmountx2Filter';
const CUSTOM_FIELDS_FILTER_PARAM_KEY = 'customFieldsFilter';

const testsSearchFields = [
	MsdkReportRunsTextSearchFields.TestName,
	MsdkReportRunsTextSearchFields.RunTime,
	MsdkReportRunsTextSearchFields.CriticalIssues,
	MsdkReportRunsTextSearchFields.TotalIssues,
	MsdkReportRunsTextSearchFields.App,
	MsdkReportRunsTextSearchFields.AppVersion,
	MsdkReportRunsTextSearchFields.Os,
	MsdkReportRunsTextSearchFields.ProductVersion,
	MsdkReportRunsTextSearchFields.SdkType,
	MsdkReportRunsTextSearchFields.CustomFields,
	MsdkReportRunsTextSearchFields.DeviceManufacturer,
	MsdkReportRunsTextSearchFields.DeviceModel,
	MsdkReportRunsTextSearchFields.DeviceType,
	MsdkReportRunsTextSearchFields.OsVersion,
	MsdkReportRunsTextSearchFields.AppBuildNumber
];

export const buildMobileSdkUsageDataVariables = (
	filters: IMobileSdkTestsFilterOption
): MobileSdkUsageDataQueryVariables => {
	const variablesBuilder = new QueryVariablesBuilder<MobileSdkUsageDataQueryVariables>({
		searchFields: testsSearchFields
	});

	const { searchValue, sessionsPeriod } = filters;
	const testsSinglePeriod = sessionsPeriod?.[0];

	// omit all the known filter keys.
	// this will leave us with the dynamic filter keys - the ones configured in the CC for labels
	const dynamicFilterOptions: TFilterOptions = omit(filters ?? {}, [
		'sessionsPeriod',
		'searchValue',
		'platform',
		'appName',
		'appVersion',
		'sdkType',
		'sdkVersion',
		'severity'
	]);

	const searchValues = preprocessSearchValue(searchValue, true);

	variablesBuilder
		.addTextSearchParam(searchValues)
		.addTupleFilterParam(
			CUSTOM_FIELDS_FILTER_PARAM_KEY,
			dynamicFilterOptions,
			ConditionOperators.All
		);

	// Add dedicated filter fields
	if (filters?.platform?.length) {
		variablesBuilder.addFilterParam('osFilter', filters.platform);
	}

	if (filters?.appName?.length) {
		variablesBuilder.addFilterParam('appFilter', filters.appName);
	}

	if (filters?.appVersion?.length) {
		variablesBuilder.addFilterParam('appVersionFilter', filters.appVersion);
	}

	if (filters?.sdkType?.length) {
		variablesBuilder.addFilterParam('sdkTypeFilter', filters.sdkType);
	}

	if (filters?.sdkVersion?.length) {
		variablesBuilder.addFilterParam('sdkVersionFilter', filters.sdkVersion);
	}

	if (
		testsSinglePeriod?.value &&
		testsSinglePeriod.value !== 'all' &&
		typeof testsSinglePeriod.value === 'number'
	) {
		variablesBuilder
			.addConditionParam(DAYS_AMOUNT_FILTER_PARAM_KEY, testsSinglePeriod.value)
			.addConditionParam(DAYS_AMOUNT_X2_FILTER_PARAM_KEY, 2 * testsSinglePeriod.value);
	}

	const variables = variablesBuilder.getVariables();
	const severityFilterVariables = buildSeverityFilterVariables<MobileSdkUsageDataQueryVariables>(
		filters?.severity
	);

	return { ...variables, ...severityFilterVariables };
};
